import React, { useEffect, useState } from 'react'
import { AlertDialog, AlertDialogContent, AlertDialogFooter, AlertDialogTrigger } from '../ui/alert-dialog'
import { AlertTriangle, Check, Loader2, Minus, Plus, Trash, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Document, Metatype, VerificationSetting } from '../../pages/PublicDocument';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { useMutation } from 'react-query';
import axios from '../../axios/axios';
import toast from 'react-hot-toast';
import { Sheet, SheetContent, SheetFooter } from '../ui/sheet';
import { Textarea } from '../ui/textarea';
import { useTranslation } from 'react-i18next';
import { getWindevLang } from 'src/lib/lang';
import { CONFIG } from 'src/config';
import lodash from 'lodash'

type Props = { 
    open: boolean;
    onOpenChange: (open:boolean) => void;
    settings?: VerificationSetting[] | null;
    metatypes?: Metatype[];
    document?: Document | null
    token?: string
    onSuccess: () => void;
}

type Meta = {
    metatype: string;
    metavalue: string;
}

const Approve = ({open, onOpenChange, settings, metatypes, document, token, onSuccess}:Props) => {
    //const groups = [['DATE', 'DTTOT']]
    const { t, i18n } = useTranslation()
    const groups:any[] = CONFIG.workflowGroups

    const [values, setValues] = useState<Meta[][]>([]);
    const [remark, setremark] = useState('');

    const { mutate:handleSendFlow, reset, isError, isLoading, isSuccess } = useMutation({
        mutationFn: async () => {
            window.localStorage.removeItem(`values`)

            let metadata:{metatype:string, metavalue:string}[] = []
            
            if(CONFIG.workflowGroupsFitdoc === "trailing-type"){
                metadata = transformTrailingTypeMetadata([...values])
            }
            else if(CONFIG.workflowGroupsFitdoc === "prefix-value"){
                metadata = values.map((x, i)=>(x.map((y)=>({metatype:y.metatype, metavalue:`[${i}] - ${y.metavalue}`})))).flat()
            }
            else{
                metadata = values.map((x, i)=>(x.map((y)=>({metatype:y.metatype, metavalue:`[${i}] - ${y.metavalue}`})))).flat()
            }

            const { data } = await axios.post(`wf/verify`, {
                // token: token,
                workflows: [{
                    docid: document?.docid,
                    remark: remark,
                    state: 1,
                    metadata: metadata
                }]
            })
            if(data.errorcode!==0){ throw new Error(data.message) }
            toast.success(t('Goedgekeurd'), {id:'wf'})
            handleCloseSheet()
            onSuccess()
            return true;
        },
        onError: (e:any) => { 
            toast.error(`${t('Er liep iets mis')}`, {id:'wf'})
            setTimeout(()=>{ reset() }, 2000)
        },
        onSuccess: () => {
            setValues([])
            window.localStorage.removeItem(`values`)
            reset()
        }
    })

    useEffect(()=>{
        if(!open){ window.localStorage.setItem(`values`, JSON.stringify(values)) }
        else {
            let v = window.localStorage.getItem(`values`);
            if(v){ 
                try {
                    const val = JSON.parse(v);
                    if(val.length > 0){
                        setValues(val)
                    }
                } catch (error) {}
            }
        }
    }, [open])

    useEffect(() => {
        if(!settings) return;

        const initialValues: Meta[][] = [];
        const addedGroups: string[] = [];

        // Loop through settings array and create initial values state based on groups
        settings.forEach(setting => {
            if(setting.metatype === "0"){ return; }
            const group = groups.find(g => g.includes(setting.metatype));
            if (group && !addedGroups.includes(group.join(''))) {
                // If the setting belongs to a group and the group hasn't been added yet, add a row with items of that group
                initialValues.push(group.map((metatype:any) => ({ metatype, metavalue: '' })));
                addedGroups.push(group.join(''));
            } else if (!group) {
                // If the setting does not belong to any group, add a row with that single setting
                initialValues.push([{ ...setting, metavalue: '' }]);
            }
        });

        setValues(initialValues);
    }, [settings]);

    function handleCloseSheet () {
        setremark('');
        onOpenChange(false)
    }

    const handleAddRow = (rowIndex: number) => {
        const newRow: Meta[] = [];
        values[rowIndex].forEach(meta => {
            newRow.push({ ...meta, metavalue:'' }); // Create new instances of each meta object
        });

        const count = countGroups(newRow.map(r => r.metatype))
        if(count === CONFIG.maxWorkflowClones){ toast.error('Maximum duplicates bereikt'); return; }

        setValues([...values, newRow]);
    }

    const handleRemoveRow = (index: number) => {
        const newValues = [...values];
        newValues.splice(index, 1);
        setValues(newValues);
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, rowIndex: number, metaIndex: number) => {
        const newValues = [...values];
        newValues[rowIndex][metaIndex].metavalue = event.target.value;
        setValues(newValues);
    }

    function transformTrailingTypeMetadata(values:Meta[][],metatypeGroups=CONFIG.workflowGroups) {
        // Flatten the array
        const flatValues = values.flat();

        // Initialize counters for each group
        const counters:any = {};
        metatypeGroups.forEach(group => {
            group.forEach(metatype => {
                counters[metatype] = 0;
            });
        });

        // Helper function to get the group for a metatype
        function getGroup(metatype:string) {
            for (let group of metatypeGroups) {
                if (group.includes(metatype)) {
                    return group;
                }
            }
            return null;
        }

        // Transform the values
        const transformed = flatValues.map(item => {
            const group = getGroup(item.metatype);
            if (group) {
                counters[item.metatype]++;
                return {
                    ...item,
                    metatype: `${item.metatype}${counters[item.metatype]}`
                };
            } else {
                return item;
            }
        });

        return transformed;
    }

    function countGroups (metagroup:string[]) {
        let amount = 0;

        values.forEach((row) => {
            const rowGroup = row.map((r)=>r.metatype)
            if(lodash.isEqual(metagroup, rowGroup)){
                amount++
            }
        })

        return amount
    }

    const isFormComplete = (values: Meta[][]): boolean => {
        const required = settings?.filter(i=>i.required).map(v=>v.metatype) || []

        for (const row of values) {
            for (const meta of row) {
                if (required.includes(meta.metatype) && meta.metavalue === '') {
                    return false; // Required metatype found without a value
                }
            }
        }
        return true; // All required metatypes have values
    };

    function convertToName(metatype:string) {
        return metatypes?.find(i=>i.metatype===metatype)?.metatype_oms.find(i=>i.nationid===getWindevLang(i18n.language))?.oms || metatype
    }

    function metatypeToInputType(metatype:string) {
        let type = 'text'
        switch (metatypes?.find(m=>m.metatype===metatype)?.datatype) {
            case 'date':
                type = 'date'
                break;
            case 'string':
                type = 'text'
                break;
            case 'integer':
                type = 'number'
                break;
            case 'currency':
                type = 'number'
                break;
            default:
                break;
        }
        return type
    }

  return (
    <Sheet open={open} onOpenChange={(o)=>{ if(!o){handleCloseSheet()} else {onOpenChange(o)} }}>
        <SheetContent position="left" hideOverlay className='flex flex-col max-w-lg gap-4 overflow-auto backdrop-blur-none w-fit'>
            <div className='flex items-center justify-between'>
                <p className='text-lg font-medium'>{t('Documenten goedkeuren')}</p>
                {/* <button onClick={()=>{onOpenChange(false)}} className='flex items-center justify-center w-8 h-8'><X className='w-5 h-5 text-neutral-500'/></button> */}
            </div>
            
            <div className='p-2 border rounded-md'>
                <p className='text-sm font-medium text-neutral-500'>{t('Geselecteerd document')}</p>
                <p>{document?.filename}</p>
            </div>

            <div>
                {values.map((row, rowIndex) => (
                <div key={`${row[0].metatype}${rowIndex}`} className='flex items-center justify-between gap-4 py-3 border-b'>
                    <div className='flex flex-wrap w-full gap-2 p-1 overflow-auto'>
                        {row.filter(i=>metatypes?.some(m=>m.metatype===i.metatype)).map((meta, metaIndex) => {
                        return (
                            <div key={`${meta.metatype}-${metaIndex}`} className='flex-[1_1_40%] box-border'>
                                <Label>{convertToName(meta.metatype)} {(settings?.find(s => s.metatype===meta.metatype)?.required) && <span className='text-red-600'>*</span>}</Label>
                                <Input 
                                    type={metatypeToInputType(meta.metatype)} 
                                    value={meta.metavalue}
                                    onChange={(event) => handleInputChange(event, rowIndex, metaIndex)}
                                />
                            </div>
                        )}
                        )}
                    </div>
                    
                    <div className='flex items-center'>
                        {!(countGroups(row.map((r)=>r.metatype)) === CONFIG.maxWorkflowClones) &&
                        <button onClick={() => handleAddRow(rowIndex)} className='flex items-center justify-center w-8 h-8 text-green-600 rounded-full hover:text-white hover:bg-green-600'><Plus className='w-4 h-4'/></button>
                        }
                        {(values.findIndex(v=>v[0].metatype===row[0].metatype) !== rowIndex) && <button onClick={() => handleRemoveRow(rowIndex)} className='flex items-center justify-center w-8 h-8 text-red-600 rounded-full hover:text-white hover:bg-red-600'><Trash className='w-4 h-4'/></button>}
                    </div>
                </div>
                ))}

            <div className='mt-4'>
                <Label htmlFor='remark'>{t('Opmerking')} {CONFIG.workflow.approve.requiredRemark && <span className='text-red-600'>*</span>}</Label>
                <Textarea value={remark} onChange={({currentTarget})=>{setremark(currentTarget.value)}} id='remark'/>
            </div>

            </div>

            <SheetFooter>
                <Button onClick={()=>{onOpenChange(false)}} variant='ghost'>{t('Annuleren')}</Button>
                <Button onClick={()=>{handleSendFlow()}} disabled={!isFormComplete(values) || isError || isLoading || isSuccess || (CONFIG.workflow.approve.requiredRemark && remark.length === 0)} className='gap-2'>
                    {isSuccess && <Check className='w-4 h-4'/>}
                    {isLoading && <Loader2 className='w-4 h-4 animate-spin'/>}
                    {isError && <AlertTriangle className='w-4 h-4'/>}
                    {t('Goedkeuren')}
                </Button>
            </SheetFooter>
        </SheetContent>
    </Sheet>
  )
}

export default Approve